import React from 'react';
import { ChevronDown } from 'lucide-react';

export const BRAND_COLORS = {
    primary: '#61257b',
    primaryDark: '#451759',
    secondary: '#c1348b',
    accent: '#ea8406',
    accentLight: '#ffd97c',
};

export interface BrandStripesProps {
    variant?: 'default' | 'scroll-prompt' | 'hero' | 'minimal';
    size?: 'sm' | 'md' | 'lg' | 'xl';
    align?: 'center' | 'left' | 'right';
    className?: string;
    style?: React.CSSProperties;
    showScrollPrompt?: boolean;
    promptText?: string;
    targetId?: string;
    onClick?: () => void;
    animated?: boolean;
}

export const BrandStripes: React.FC<BrandStripesProps> = ({
    variant = 'default',
    size = 'md',
    align = 'center',
    className = '',
    style = {},
    showScrollPrompt = false,
    promptText = 'Desliza para explorar',
    targetId,
    onClick,
    animated = true,
}) => {
    // Configuración de tamaños
    const sizeConfig = {
        sm: { height: 3, primaryWidth: 26, dotWidth: 8, gap: 5, radius: 3, fontSize: '0.72rem', chevronSize: 13 },
        md: { height: 4, primaryWidth: 38, dotWidth: 12, gap: 6, radius: 4, fontSize: '0.8rem', chevronSize: 15 },
        lg: { height: 5, primaryWidth: 50, dotWidth: 16, gap: 8, radius: 5, fontSize: '0.88rem', chevronSize: 18 },
        xl: { height: 6, primaryWidth: 64, dotWidth: 20, gap: 10, radius: 6, fontSize: '0.95rem', chevronSize: 20 },
    }[size];

    const isScrollPrompt = variant === 'scroll-prompt' || showScrollPrompt;

    const handleScrollClick = () => {
        if (onClick) {
            onClick();
            return;
        }

        if (targetId) {
            const el = document.getElementById(targetId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
                return;
            }
        }

        // Si no hay targetId específico, scroll hacia abajo suavemente
        window.scrollBy({
            top: window.innerHeight * 0.7,
            behavior: 'smooth',
        });
    };

    const stripesData = [
        {
            color: BRAND_COLORS.primary,
            gradient: `linear-gradient(90deg, ${BRAND_COLORS.primaryDark}, ${BRAND_COLORS.primary})`,
            glow: 'rgba(97, 37, 123, 0.55)',
            width: sizeConfig.primaryWidth,
            delay: '0s',
        },
        {
            color: BRAND_COLORS.secondary,
            gradient: `linear-gradient(90deg, ${BRAND_COLORS.secondary}, #d949a2)`,
            glow: 'rgba(193, 52, 139, 0.55)',
            width: sizeConfig.dotWidth,
            delay: '0.15s',
        },
        {
            color: BRAND_COLORS.accent,
            gradient: `linear-gradient(90deg, ${BRAND_COLORS.accent}, #ff9d26)`,
            glow: 'rgba(234, 132, 6, 0.55)',
            width: sizeConfig.dotWidth,
            delay: '0.3s',
        },
        {
            color: BRAND_COLORS.accentLight,
            gradient: `linear-gradient(90deg, ${BRAND_COLORS.accentLight}, #ffe49e)`,
            glow: 'rgba(255, 217, 124, 0.7)',
            width: sizeConfig.dotWidth,
            delay: '0.45s',
        },
    ];

    const justifyMap = {
        center: 'center',
        left: 'flex-start',
        right: 'flex-end',
    };

    return (
        <div
            className={`comarca-brand-stripes-container ${className}`}
            style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: align === 'center' ? 'center' : align === 'left' ? 'flex-start' : 'flex-end',
                cursor: (isScrollPrompt || onClick || targetId) ? 'pointer' : 'default',
                userSelect: 'none',
                ...style,
            }}
            onClick={isScrollPrompt || onClick || targetId ? handleScrollClick : undefined}
            role={isScrollPrompt || onClick || targetId ? 'button' : undefined}
            tabIndex={isScrollPrompt || onClick || targetId ? 0 : undefined}
            onKeyDown={(e) => {
                if ((isScrollPrompt || onClick || targetId) && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleScrollClick();
                }
            }}
        >
            <style>{`
                @keyframes comarcaLightSweep {
                    0% {
                        left: -120%;
                        opacity: 0;
                    }
                    15% {
                        opacity: 1;
                    }
                    60% {
                        left: 140%;
                        opacity: 1;
                    }
                    100% {
                        left: 140%;
                        opacity: 0;
                    }
                }

                @keyframes comarcaStripePulse {
                    0%, 100% {
                        transform: scaleY(1) translateY(0);
                        filter: brightness(1);
                    }
                    50% {
                        transform: scaleY(1.22) translateY(-1px);
                        filter: brightness(1.25) saturate(1.2);
                    }
                }

                @keyframes comarcaGlowBreath {
                    0%, 100% {
                        opacity: 0.35;
                        transform: scale(0.95);
                    }
                    50% {
                        opacity: 0.85;
                        transform: scale(1.1);
                    }
                }

                @keyframes comarcaScrollBounce {
                    0%, 100% {
                        transform: translateY(0);
                    }
                    50% {
                        transform: translateY(6px);
                    }
                }

                @keyframes comarcaChevronFlow {
                    0% {
                        opacity: 0.3;
                        transform: translateY(-3px);
                    }
                    50% {
                        opacity: 1;
                        transform: translateY(3px);
                    }
                    100% {
                        opacity: 0.3;
                        transform: translateY(-3px);
                    }
                }

                .comarca-brand-stripes-wrapper {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    padding: 4px 6px;
                    border-radius: 999px;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }

                .comarca-brand-stripes-container:hover .comarca-brand-stripes-wrapper {
                    transform: translateY(-1px) scale(1.04);
                }

                .comarca-brand-stripes-container:hover .comarca-stripe-glow {
                    opacity: 0.9 !important;
                    filter: blur(5px) !important;
                }

                .comarca-stripe-item {
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
                    transition: all 0.25s ease;
                }

                .comarca-stripe-animated {
                    animation: comarcaStripePulse 3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
                }

                .comarca-light-beam {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    width: 45px;
                    background: linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255, 255, 255, 0.3) 20%,
                        rgba(255, 255, 255, 0.95) 50%,
                        rgba(255, 255, 255, 0.3) 80%,
                        transparent 100%
                    );
                    transform: skewX(-25deg);
                    pointer-events: none;
                    animation: comarcaLightSweep 2.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
                }

                .comarca-scroll-prompt-badge {
                    animation: comarcaScrollBounce 2.2s ease-in-out infinite;
                }

                .comarca-chevron-animated {
                    animation: comarcaChevronFlow 1.8s ease-in-out infinite;
                }
            `}</style>

            {/* Contenedor de las 4 franjas */}
            <div
                className={`comarca-brand-stripes-wrapper ${isScrollPrompt ? 'comarca-scroll-prompt-badge' : ''}`}
                style={{
                    gap: `${sizeConfig.gap}px`,
                    justifyContent: justifyMap[align],
                }}
            >
                {stripesData.map((stripe, index) => (
                    <div
                        key={index}
                        style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {/* Halo de luz / Resplandor de fondo */}
                        {animated && (
                            <div
                                className="comarca-stripe-glow"
                                style={{
                                    position: 'absolute',
                                    inset: '-3px -4px',
                                    borderRadius: `${sizeConfig.radius + 4}px`,
                                    background: stripe.glow,
                                    filter: 'blur(4px)',
                                    opacity: 0.45,
                                    pointerEvents: 'none',
                                    animation: `comarcaGlowBreath 3s ease-in-out infinite`,
                                    animationDelay: stripe.delay,
                                    transition: 'all 0.3s ease',
                                }}
                            />
                        )}

                        {/* Barra de color sólida con gradiente */}
                        <div
                            className={`comarca-stripe-item ${animated ? 'comarca-stripe-animated' : ''}`}
                            style={{
                                height: `${sizeConfig.height}px`,
                                width: `${stripe.width}px`,
                                borderRadius: `${sizeConfig.radius}px`,
                                background: stripe.gradient,
                                animationDelay: stripe.delay,
                            }}
                        >
                            {/* Haz de luz reflectante recorriendo la barra */}
                            {animated && (
                                <div
                                    className="comarca-light-beam"
                                    style={{
                                        animationDelay: '0.2s',
                                    }}
                                />
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Prompt de Scroll hacia abajo / Dash downwards */}
            {isScrollPrompt && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '2px',
                        marginTop: '8px',
                        transition: 'transform 0.2s ease, opacity 0.2s ease',
                    }}
                >
                    {promptText && (
                        <span
                            style={{
                                fontSize: sizeConfig.fontSize,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                color: BRAND_COLORS.primaryDark,
                                opacity: 0.85,
                                textTransform: 'uppercase',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            {promptText}
                        </span>
                    )}

                    {/* Flecha hacia abajo con flujo de luz */}
                    <div
                        className="comarca-chevron-animated"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: BRAND_COLORS.secondary,
                            filter: `drop-shadow(0 2px 6px ${BRAND_COLORS.secondary}55)`,
                        }}
                    >
                        <ChevronDown size={sizeConfig.chevronSize} strokeWidth={2.8} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default BrandStripes;
